import { ChangeDetectorRef, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import * as _ from 'lodash';
import { ConfirmationService, MenuItem, MessageService } from 'primeng/api';
import { AppIcons } from '../../../shared/app.icons';
import { AppSettings } from '../../../shared/app.settings';
import { MITableComponent } from '../../../shared/components/mi-table/mi-table.component';
import type { Country } from '../../../shared/models/country';
import type { Language } from '../../../shared/models/language';
import { CommonBindingDataService } from '../../../shared/services/common-binding-data.service';
import { EntityService } from '../../../shared/services/entity.service';
import type { EntityList } from '../../models/entity.models';

import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AccessProviderDirective } from 'app/modules/shared/directives/access-provider.directive';
import { MetaDataService } from 'app/modules/shared/services/meta-data.service';
import { PermissionAccessService } from 'app/modules/shared/services/permission-access.service';
import { RestApiService } from 'app/modules/shared/services/rest-api.service';
import { StorageService } from 'app/modules/shared/services/storage.service';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { forkJoin } from 'rxjs';
import { ModuleHeaderComponent } from '../../../shared/components/module-header/module-header.component';
import { ConfigService } from '../../../shared/services/config.service';
import { LoaderService } from 'app/modules/shared/components/loader/loader.service';
import { ListResponse } from '../../models/listResponse.models';
import { ToastModule } from 'primeng/toast';
import { DownloadService } from 'app/modules/shared/services/download.service';
import { TabViewModule } from 'primeng/tabview';

@Component({
  selector: 'app-order-erp-list',
  standalone: true,
  imports: [BreadcrumbModule, ModuleHeaderComponent, TranslateModule, MITableComponent, AccessProviderDirective, ToastModule, TabViewModule],
  templateUrl: './order_erp-list.component.html',
  styleUrl: './order_erp-list.component.scss'
})
export class OrderErpListComponent implements OnInit {
  @ViewChild('uploader', { static: true }) uploader: ElementRef<HTMLElement>;
  @ViewChild(MITableComponent) tableRef: MITableComponent;
  activeIndex: number = 0;
  miIcons = AppIcons;
  isLoading = true;
  filterAvailableColumns = [];
  cols = [];
  moduleName = 'order_erp';
  filterList;
  btnLabel: string;
  items: MenuItem[] | undefined;
  isShowMenu: boolean = true;
  attributeLabels = {};
  filterAttributeLabels = {};
  listColumns: any;
  allColumns: any;
  pagination: any;
  totalRecords: number = 0;
  tableData: any = [];
  buttons: any[] = [];
  country: Country;
  language: Language;
  entityCount: any;
  itemsPerPage: number;
  entityData: EntityList = {
    limit: AppSettings.PAGINATION_ROWS_PER_PAGE_LIMIT,
    offset: 0,
    searchStr: "",
    filters: [],
    countryCode: '',
    deleted: AppSettings.DELETED_TYPE.ONLY_NON_DELETED,
    forTenantCode: "",
    actionStatus: ''
  };
  routePath: MenuItem[] = [];
  deleteOrderErpMsg: string;
  deleteOrderErpHeaderMsg: string;
  showMenu: boolean = false;
  moduleHeaderSecondaryBtnMenuItems: MenuItem[] = [];
  filterFieldsArray: any[];
  isReady: boolean = false;
  attributeData: any;
  data;
  rowItems: MenuItem[] | undefined;
  bulkSelectionsItems: MenuItem[] | undefined;
  currentRow;
  visiblePrintLabel: boolean = false;
  showBulkUpdateDialog: boolean = false;
  visible: boolean = false;
  bulkUpdateHeader;
  reviewHeader: any;
  btnUpdate: any;
  entityCreatedAtAttributeId;
  regionGeofences: any = [];
  zoneIds: any = [];
  isEntity = true;
  orderErpStatusAttributeId: string;
  showImportExportBtn: boolean = false;
  currentRowId;
  protocolIdAttributeId: number;
  protocolDefaultSortColumnAttributeId: number;
  siteDefaultSortColumnAttributeId: number;
  productDefaultSortColumnAttributeId: number;
  trialId: string;
  btnDownloadInvoice: string;
  private exportInProgress = false;
  isAddOrderAllowed: boolean = false;
  userDetailsById: any[] = [];
  usersData: any[] = [];

  constructor(private orderErpService: EntityService,
    private cs: CommonBindingDataService, private confirmationService: ConfirmationService,
    private router: Router, private messageService: MessageService, private actRoute: ActivatedRoute,
    private metaDataService: MetaDataService,
    private restApiService: RestApiService,
    private configService: ConfigService, private permissionAccessService: PermissionAccessService,
    private cd: ChangeDetectorRef, private loaderService: LoaderService,
    private storageService: StorageService, private translateService: TranslateService,
    private downloadService: DownloadService,
  ) { }

  ngOnInit() {
    this.actRoute.queryParams.subscribe(param => {
      this.trialId = param?.id
    });

    this.initializeLabels();
    this.initializeRowItems();
    this.initializeModuleHeaderSecondaryBtnMenuItems();
    this.initializeOtherVariablesAndAPI();
    this.getData();
    this.getMetaData();
  }

  getMetaData() {
    const payload = this.getMultiApiPayload();
    this.metaDataService.metaDataMultipleApi(payload).subscribe((res) => {
      const advancedFilter = JSON.parse(res.advancedFilter.responseBody);
      if (advancedFilter) {
        this.setOrderErpFilterView(advancedFilter);
      }

      const sort = JSON.parse(res.sort.responseBody);
      const entityCreatedAt = sort.tabs[0].groups[0].fields.find(field => field.attributeCode === AppSettings.ENTITY_CREATED_AT);
      this.entityCreatedAtAttributeId = entityCreatedAt.attributeId;
      if (this.entityCreatedAtAttributeId) {
        this.entityData.defaultSortType = AppSettings.DESC_SORT_ORDER;
      }

      const defaultTableView = JSON.parse(res.defaultTableView.responseBody);
      if (defaultTableView) {
        this.setOrderErpTableView(defaultTableView);
      }
      this.cs.applyFilterToColumn(defaultTableView, sort)
    });
  }

  private getMultiApiPayload() {
    return [
      {
        url: `${AppSettings.BASE_URL}${this.configService.getLoggedInTenantCode()}/api/v1/entity-conf/${AppSettings.ENTITY_CODE.ORDER_ERP}/attributes?forTenantCode=${this.configService.getForTenantCode()}&viewCode=${AppSettings.VIEW_CODE.ADVANCED_FILTER_VIEW}`,
        inputBody: '',
        method: 'GET',
        outputKey: AppSettings.META_OUTPUT_KEY.ADVANCED_FILTER,
        headers: this.restApiService.getHeaderForMultipleAPI()
      },
      {
        url: `${AppSettings.BASE_URL}${this.configService.getLoggedInTenantCode()}/api/v1/entity-conf/${AppSettings.ENTITY_CODE.ORDER_ERP}/attributes?forTenantCode=${this.configService.getForTenantCode()}&viewCode=${AppSettings.VIEW_CODE.DEFAULT_SORT_VIEW}`,
        inputBody: '',
        method: 'GET',
        outputKey: AppSettings.META_OUTPUT_KEY.SORT,
        headers: this.restApiService.getHeaderForMultipleAPI()
      },
      {
        url: `${AppSettings.BASE_URL}${this.configService.getLoggedInTenantCode()}/api/v1/entity-conf/${AppSettings.ENTITY_CODE.ORDER_ERP}/attributes?forTenantCode=${this.configService.getForTenantCode()}&viewCode=${AppSettings.VIEW_CODE.DEFAULT_TABLE_VIEW}`,
        inputBody: '',
        method: 'GET',
        outputKey: AppSettings.META_OUTPUT_KEY.DEFAULT_TABLE_VIEW,
        headers: this.restApiService.getHeaderForMultipleAPI()
      },
    ]
  }

  private initializeRowItems() {
    const isEdit = this.permissionAccessService.hasAccess('ORDER_ERP', '', 'ALL', 'EDIT');
    const isDelete = this.permissionAccessService.hasAccess('ORDER_ERP', '', 'ALL', 'DELETE');
    const isActivate = this.permissionAccessService.hasAccess('ORDER_ERP', '', 'ALL', 'ACTIVATE');
    this.rowItems = [
      ...(isEdit ? [{
        label: this.translateService.instant('order_erp.lbl_edit'),
        icon: `${AppIcons.EDIT_2} wh-16`,
        iconClass: 'bulk-update-icon',
        value: 'edit',
        command: () => {
          this.update();
        }
      }] : []),
      ...(isActivate ? [{
        label: this.translateService.instant('order_erp.lbl_activate'),
        icon: `${AppIcons.CIRCLE_CHECKED} wh-16`,
        iconClass: 'bulk-update-icon',
        value: 'active',
        command: () => {
          this.onUpdateStatus('Active', this.translateService.instant('order_erp.lbl_activate'));
        }
      }] : []),
      ...(isActivate ? [{
        label: this.translateService.instant('order_erp.lbl_deactivate'),
        icon: `${AppIcons.ACTIVATE} wh-16`,
        iconClass: 'bulk-update-icon',
        value: 'deactive',
        command: () => {
          this.onUpdateStatus('Inactive', this.translateService.instant('order_erp.lbl_deactivate'));
        }
      }] : []),
      { separator: true },
      ...(isDelete ? [{
        label: this.translateService.instant('order_erp.lbl_delete'),
        icon: `${AppIcons.BASIC_TRAY} wh-16`,
        iconClass: 'bulk-update-icon',
        value: 'delete',
        command: () => {
          this.onDeleteOrderErp();
        }
      }] : []),
    ];
  }

  onUpdateStatus(status, acceptLabelText) {
    const isActive = status === 'Active';
    this.confirmationService.confirm({
      header: this.translateService.instant(isActive ? 'order_erp.lbl_activate_confirmation' : 'order_erp.lbl_deactivate_confirmation'),
      message: this.translateService.instant(isActive ? 'order_erp.msg_activate_confirmation' : 'order_erp.msg_deactivate_confirmation'),
      acceptIcon: 'none',
      rejectIcon: 'none',
      rejectButtonStyleClass: 'bg-white text-color cancel',
      acceptButtonStyleClass: 'bg-red-500 delete',
      acceptLabel: acceptLabelText,
      rejectLabel: this.translateService.instant('lbl_cancel'),
      accept: () => {

        const orderErpData = {
          forTenantCode: this.configService.getForTenantCode(),
          entityCode: this.moduleName,
          countryCode: this.country[0].countryCode,
          languageCode: this.language?.[0]?.langCode || 'en',
          data: [{
            attributeId: this.orderErpStatusAttributeId,
            attributeValue: status
          }],
          relationshipData: [],
          viewCode: AppSettings.VIEW_CODE.STATUS_UPDATE_VIEW,
        };

        this.orderErpService.saveAttributeData(orderErpData.entityCode, this.currentRow.id, orderErpData).subscribe({
          next: () => {
            this.messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: this.translateService.instant('order_erp.lbl_activate_successfully'),
            });
            setTimeout(() => {
              this.searchEntity();
            }, 300);
          },
          error: () => {
            this.messageService.add({
              severity: 'error',
              summary: this.translateService.instant('order_erp.lbl_failed_status'),
              detail: this.translateService.instant('order_erp.lbl_failed_status')
            });
          }
        });
      },
      reject: () => {
      }
    });
  }


  private openBulkUpdateDialog() {
    this.showBulkUpdateDialog = false;
    this.cd.detectChanges();
    setTimeout(() => {
      this.showBulkUpdateDialog = true;
      this.cd.detectChanges();
      this.tableRef.showBulkRowDialog();
    }, 0);
  }

  handleBulkUpdate() {
    this.visible = true;
    this.showBulkUpdateDialog = true;
  }

  handleBulkPrint() {
    this.visiblePrintLabel = true;
  }

  private initializeModuleHeaderSecondaryBtnMenuItems() {
  }

  downloadImportTemplate() {
    this.orderErpService.downloadImportTemplate(AppSettings.ENTITY_CODE.ORDER_ERP).subscribe(result => {
      const a = document.createElement('a');
      a.href = URL.createObjectURL(result);
      a.download = 'Import Template.xlsx';
      a.click();
    },
      () => {
      });
  }

  downloadImportGuide() {
    this.orderErpService.downloadImportGuide(AppSettings.ENTITY_CODE.ORDER_ERP).subscribe(result => {
      const a = document.createElement('a');
      a.href = URL.createObjectURL(result);
      a.download = 'Import Guide.xlsx';
      a.click();
    },
      () => {
      });
  }

  private initializeLabels() {
    this.entityData.forTenantCode = this.configService.getForTenantCode();
    this.bulkUpdateHeader = this.translateService.instant('lbl_update_order_erp');
    this.reviewHeader = this.translateService.instant('lbl_review_order_erp');
    this.btnUpdate = this.translateService.instant('btn_update_order_erp');
    this.routePath = [{ label: this.translateService.instant('order_erp.lbl_order_erp'), routerLink: 'order_erp/list' }];
    this.btnDownloadInvoice = this.translateService.instant('order_erp.download_invoice');
  }

  private initializeOtherVariablesAndAPI() {
    this.btnLabel = this.translateService.instant('order_erp.add_order_erp');
    this.country = JSON.parse(localStorage.getItem(AppSettings.COUNTRY));
    this.language = JSON.parse(localStorage.getItem(AppSettings.LANGUAGE));
    this.deleteOrderErpMsg = this.translateService.instant('order_erp.message.confirm_delete_msg');
    this.deleteOrderErpHeaderMsg = this.translateService.instant('order_erp.message.confirm_delete_header');
  }

  public onChange(event) {
    for (const file of event.target.files) {
      if (file) {
        if (file.size > AppSettings.FILE_UPLOAD_MAX_SIZE_IN_BYTE) {
          this.messageService.add({ severity: 'error', summary: 'Error', detail: this.translateService.instant('err_file_size_exceed_msg') });
        } else {
          const formData = new FormData();
          formData.append("file", file);
          formData.append('forTenantCode', this.configService.getForTenantCode());
          formData.append('entityCode', AppSettings.ENTITY_CODE.ORDER_ERP);
          formData.append('viewCode', AppSettings.VIEW_CODE.EXCEL_IMPORT_VIEW);
          this.cs.importEntity(formData, AppSettings.ENTITY_CODE.ORDER_ERP).subscribe(() => {
            this.messageService.add({ key: 'tst', severity: 'success', summary: 'Success', detail: this.translateService.instant('lbl_file_uploaded_successfully') });
          })
        }
      }
    }
  }

  getAttributeData() {
    this.orderErpService.getAttributeDefinition(AppSettings.ENTITY_CODE.ORDER_ERP, AppSettings.VIEW_CODE.BULK_UPDATE_VIEW).subscribe(res => {
      if (res) {
        this.setOrderErpView(res);
      }
    });
  }

  setOrderErpView(res) {
    this.attributeData = res;
    this.isLoading = false;
    this.filterFieldsArray = res.tabs[0].groups[0].fields;
    this.filterFieldsArray.forEach(element => {
      switch (element.attributeCode) {
        case 'order_erp_type':
          element.attributeValue = 'Type';
          element.isDisable = false;
          break;
        case 'order_erp_status':
          element.attributeValue = 'Status';
          element.isDisable = false;
          break;
      }
    });
  }

  setCurrentRowData(event) {
    this.currentRow = event;
    this.currentRowId = event.id;
    if (event.deleted) {
      this.rowItems.forEach(item => {
        if (!item.separator) {
          item.disabled = true;
        }
      });
      return;
    }

    this.rowItems.forEach(item => {
      switch (item.value) {
        case 'active':
          item.disabled = event.order_erp_status?.Active === 'Active';
          break;
        case 'deactive':
          item.disabled = event.order_erp_status?.Inactive === 'Inactive';
          break;
        case 'edit':
        case 'delete':
          item.disabled = false;
          break;
      }
    });
  }

  onDeleteOrderErp() {
    this.confirmationService.confirm({
      header: this.deleteOrderErpHeaderMsg,
      message: this.deleteOrderErpMsg,
      acceptIcon: 'none',
      rejectIcon: 'none',
      rejectButtonStyleClass: 'bg-white text-color cancel',
      acceptButtonStyleClass: 'bg-red-500 delete',
      acceptLabel: this.translateService.instant('lbl_delete'),
      rejectLabel: this.translateService.instant('lbl_cancel'),
      accept: () => {
        const requestBody = {
          forTenantCode: this.configService.getForTenantCode(),
          countryCode: AppSettings.HEADER_COUNTRY_CODE,
          entityCode: AppSettings.ENTITY_CODE.ORDER_ERP,
          entityIds: [this.currentRowId]
        };

        this.orderErpService.deleteEntityActionStatus(requestBody).subscribe({
          next: () => {
            this.cs.onDisplayMessage(this.data, 'order_erp', AppSettings.MSG_ACTION.DELETE, 'success');
            setTimeout(() => {
              this.searchEntity();
            }, 300);
          },
          error: () => {
            this.cs.onDisplayMessage(this.data, 'order_erp', AppSettings.MSG_ACTION.DELETE, 'error');
          }
        });
      }
    });
  }

  update() {
    this.onUpdateOrderErp(this.currentRow.id);
  }

  getTableView() {
    this.orderErpService.getAttributeDefinition(AppSettings.ENTITY_CODE.ORDER_ERP, AppSettings.VIEW_CODE.DEFAULT_TABLE_VIEW, false).subscribe(res => {
      if (res) {
        this.setOrderErpTableView(res);
      }
    });
  }

  setOrderErpTableView(res) {
    this.isLoading = false;
    this.data = res;
    this.orderErpStatusAttributeId = res.tabs[0].groups[0].fields.find(field => field.attributeCode === 'order_erp_status')?.attributeId;

    this.attributeLabels = this.cs.getAttributeLabels(this.data);
    this.allColumns = this.cs.getColumns(this.data).filter(column =>
      !['entity_created_at'].includes(column.attributeCode)
    );

    this.searchEntity();
    this.allColumns?.forEach((key, index) => {
      this.allColumns[index] = key;
      this.allColumns[index].field = key.attributeCode;
      this.allColumns[index].header = this.cs.getLabelValue(
        `order_erp` + `.fields.${key.attributeCode}.label`,
        this.attributeLabels,
        key.attributeCode
      );
    });
    this.filterAvailableColumns = _.clone(this.allColumns);
  }

  getFilterView() {
    this.orderErpService.getAttributeDefinition(AppSettings.ENTITY_CODE.ORDER_ERP, AppSettings.VIEW_CODE.ADVANCED_FILTER_VIEW).subscribe(filterResponse => {
      if (filterResponse) {
        this.setOrderErpFilterView(filterResponse);
      }
    });
  }

  setOrderErpFilterView(filterResponse) {


    this.orderErpService.getAttributeDefinition(AppSettings.ENTITY_CODE.PROTOCOL, AppSettings.VIEW_CODE.DEFAULT_SORT_VIEW)
      .subscribe(sortColumnResponse => {
        this.filterAttributeLabels = this.cs.getAttributeLabels(filterResponse);
        this.filterList = this.cs.getColumns(filterResponse);
        this.protocolIdAttributeId = this.filterList.find(field => field.attributeCode === 'protocol_entity_id')?.attributeId as number;
        this.filterList = _.sortBy(this.filterList, 'attributeIndex');
        if (sortColumnResponse) {
          this.protocolDefaultSortColumnAttributeId = this.cs.getColumns(sortColumnResponse)?.find(element => element.attributeCode === 'protocol_name')?.attributeId;

          this.filterList.forEach(field => {
            if (field.attributeCode === 'protocol_entity_id') {
              field.defaultSortColumn = this.protocolDefaultSortColumnAttributeId;
              field.moduleName = AppSettings.ENTITY_CODE.PROTOCOL;
            }
            if (field.attributeCode === 'site_entity_id') {
              field.defaultSortColumn = this.siteDefaultSortColumnAttributeId;
              field.moduleName = AppSettings.ENTITY_CODE.SITE;
            }
            if (field.attributeCode === 'product_entity_id') {
              field.defaultSortColumn = this.productDefaultSortColumnAttributeId;
              field.moduleName = AppSettings.ENTITY_CODE.PRODUCT;
            }
          });

        }
      });




  }

  getData() {
    return forkJoin({
      tableViewData: this.getTableView(),
      filterViewDate: this.getFilterView()
    })
  }

  onSortData(sortColumnDetails) {
    this.entityData.defaultSortColumn = sortColumnDetails.attributeId;
    this.entityData.defaultSortType = (sortColumnDetails.sortOrder === 1) ? AppSettings.ASC_SORT_ORDER : AppSettings.DESC_SORT_ORDER;
    this.searchEntity();
  }

  searchEntity() {
    this.entityData.countryCode = AppSettings.HEADER_COUNTRY_CODE;
    this.loaderService.show();
    this.orderErpService.searchEntity(AppSettings.ENTITY_CODE.ORDER_ERP, this.entityData, false).subscribe((res: ListResponse) => {
      this.listColumns = res?.data[0]?.values;
      this.pagination = res?.pagination;
      this.totalRecords = res?.count;

      // Set entity count from the response
      if (res?.count) {
        this.entityCount = {
          tenantId: '',
          entityCode: AppSettings.ENTITY_CODE.ORDER_ERP,
          counts: res.count
        };
      }

      // Process data with related entities
      const processedData = res.data.map((data: any) => {
        // Extract related data for product, protocol, order, and site
        const flatRelatedValues = (data.relatedData || []).reduce((acc: any, item: any) => {
          if (item.entityCode === 'product' || item.entityCode === 'protocol' ||
            item.entityCode === 'order' || item.entityCode === 'site') {
            return {
              ...acc,
              ...(item.values || {})
            };
          }
          return acc;
        }, {});

        // Extract specific product data
        const productData = (data.relatedData || [])
          .filter(item => item.entityCode === 'product')
          .map(item => ({
            id: item.id,
            entityCode: item.entityCode,
            ...(item.values || {})
          }));

        // Extract specific protocol data  
        const protocolData = (data.relatedData || [])
          .filter(item => item.entityCode === 'protocol')
          .map(item => ({
            id: item.id,
            entityCode: item.entityCode,
            ...(item.values || {})
          }));

        // Extract specific order data
        const orderData = (data.relatedData || [])
          .filter(item => item.entityCode === 'order')
          .map(item => ({
            id: item.id,
            entityCode: item.entityCode,
            ...(item.values || {})
          }));

        // Extract specific site data
        const siteData = (data.relatedData || [])
          .filter(item => item.entityCode === 'site')
          .map(item => ({
            id: item.id,
            entityCode: item.entityCode,
            ...(item.values || {})
          }));

        return {
          id: data.id,
          actionStatus: data?.actionStatus || '',
          ...(data.values || {}),
          ...flatRelatedValues,
          product: productData,
          protocol: protocolData,
          order: orderData,
          site: siteData,
          relatedData: data.relatedData || []
        };
      });
      this.entityCount = {
        counts: {
          ALL: res.count || 0
        }
      };

      const orderIds = processedData.map(data => data.received_by);
      if (orderIds.length > 0) {
        this.getUserDetailsById(orderIds, processedData);
      } else {
        this.tableData = processedData;
        this.loaderService.hide();
      }
    })
  }

  getUserDetailsById(userIds: string[], rawData?: any[]) {
    this.orderErpService.getUserDetailsById(userIds).subscribe(res => {
      this.usersData = res as any[];
      this.userDetailsById = this.usersData.map((user: any) => {
        const fullName = `${user.firstName} ${user.lastName}`;

        const roleName = user.rolelist && user.rolelist.length > 0
          ? user.rolelist[0]?.roleName || '--'
          : '--';

        return {
          userId: user.userId,
          fullName,
          roleName
        };
      });

      const dataToProcess = rawData || this.tableData;
      this.tableData = dataToProcess.map(order => {
        const userDetail = this.userDetailsById.find(user => user.userId === order.received_by);
        if (userDetail) {
          return {
            ...order,
            received_by: `By ${userDetail.fullName} (${userDetail.roleName})`
          };
        }
        return order;
      });

      this.loaderService.hide();
    });
  }

  onAddOrderErp() {
    this.router.navigate(['app/order_erp/add-order_erp',]);
  }

  getLabel(params) {
    return `${params}hi`;
  }

  onRowClicked() {
  }

  onActionBtnClicked() {
  }

  onPageChange(event) {
    this.entityData.offset = event?.first;
    this.entityData.limit = event?.rows;
    this.searchEntity();
  }

  itemPerPageChange(event) {
    this.itemsPerPage = event;
    this.entityData.limit = this.itemsPerPage;
    this.searchEntity();
  }

  onConfirmDelete() {
  }

  onOrderErpDelete(event) {
    const requestBody = {
      forTenantCode: this.configService.getForTenantCode(),
      countryCode: AppSettings.HEADER_COUNTRY_CODE,
      entityCode: AppSettings.ENTITY_CODE.ORDER_ERP,
      entityIds: [
        event
      ]
    };
    this.orderErpService.deleteEntityActionStatus(requestBody).subscribe({
      next: () => {
        this.cs.onDisplayMessage(this.data, 'order_erp', AppSettings.MSG_ACTION.DELETE, 'success');
        setTimeout(() => {
          this.searchEntity();
        }, 300);
      },
      error: () => {
        this.cs.onDisplayMessage(this.data, 'order_erp', AppSettings.MSG_ACTION.DELETE, 'error');
      }
    })
  }

  onUpdateOrderErp(orderErpId) {
    this.router.navigate([`app/order_erp/add-order_erp/${orderErpId}`]);
  }

  onRowSelect(event) {
    if (event.type === 'row') {
      this.router.navigate([`app/order_erp/order_erp-details/${event.data.id}`]);
      this.orderErpService.entityCurrentRow.next(event.data);
    }
  }

  onFilterValueChange(event) {
    const vData = event;
    this.entityData.filters = [];
    if (this.protocolIdAttributeId && this.trialId) {
      this.entityData.filters.push({
        attributeId: this.protocolIdAttributeId,
        attributeValue: this.trialId
      });
    }
    for (const [key, value] of Object.entries(vData)) {
      const attributeValue = value;
      if (attributeValue) {
        this.entityData.filters.push({
          attributeCode: key,
          attributeValue
        });
      }
    }
    this.entityData.filters = _.filter(_.uniq(this.entityData.filters, (item) => {
      if (item.attributeValue.length > 0) {
        return item.attributeCode;

      }
    }), (element) => {
      if (element.attributeValue.length > 0) {
        return true;
      }
      return false;
    });
    this.entityData.offset = event.first;
    this.entityData.filters = this.cs.mapAttributeIds(this.entityData.filters, this.filterList, 'filter');
    this.searchEntity();
  }

  onSearchValueChanges(event) {
    this.entityData.searchStr = event;
    this.searchEntity();
  }



  onBulkDataUpdateEvent() {
  }

  onBulkDataDeleteEvent(event) {
    const requestBody = {
      forTenantCode: this.configService.getForTenantCode(),
      countryCode: AppSettings.HEADER_COUNTRY_CODE,
      entityCode: AppSettings.ENTITY_CODE.ORDER_ERP,
      entityIds: event
    };
    this.orderErpService.deleteEntityActionStatus(requestBody).subscribe({
      next: () => {
        this.cs.onDisplayMessage(this.data, 'order_erp', AppSettings.MSG_ACTION.DELETE, 'success');
        setTimeout(() => {
          this.searchEntity();
        }, 300);
      },
      error: () => {
        this.cs.onDisplayMessage(this.data, 'order_erp', AppSettings.MSG_ACTION.DELETE, 'error');
      }
    });
  }

  exportSelectedOrderErp() {
    if (this.exportInProgress) return;
    this.exportInProgress = true;
    setTimeout(() => {
      this.exportInProgress = false;
    }, 500);


    this.exportOrderErpData();
  }

  exportOrderErpData() {
    const siteAttributeId = this.filterList?.find(field => field.attributeCode === 'site_entity_id')?.attributeId;
    const productAttributeId = this.filterList?.find(field => field.attributeCode === 'product_entity_id')?.attributeId;
    const protocolAttributeId = this.protocolIdAttributeId || this.filterList?.find(field => field.attributeCode === 'protocol_entity_id')?.attributeId;

    const getIds = (attrId) => {
      if (!attrId) return [];
      const filter = (this.entityData.filters || []).find(f => f.attributeId === attrId);
      if (!filter || filter.attributeValue == null) return [];
      const v = filter.attributeValue;
      return Array.isArray(v) ? v : [v];
    };

    const requestBody = {
      forTenantCode: this.configService.getForTenantCode(),
      searchStr: this.entityData?.searchStr || '',
      siteIds: getIds(siteAttributeId),
      protocolIds: getIds(protocolAttributeId),
      productIds: getIds(productAttributeId)
    }

    this.loaderService.show();
    this.orderErpService.exportOrderErpData(AppSettings.ENTITY_CODE.ORDER_ERP, requestBody).subscribe({
      next: (res) => {
        this.loaderService.hide();
        if (res && this.downloadService.isValidResponse(res)) {
          try {
            this.downloadService.downloadZip(res, 'order_erp_invoice');

            this.messageService.add({
              key: 'tst',
              severity: 'success',
              summary: 'Success',
              detail: this.translateService.instant('order_erp.download_invoice_successful')
            });

            setTimeout(() => {
              this.searchEntity();
              this.tableRef.clearSelected();
            }, 300);
          } catch (error) {
            this.messageService.add({
              key: 'tst',
              severity: 'error',
              summary: 'Error',
              detail: this.translateService.instant('order_erp.download_invoice_failed')
            });
          }
        } else {
          this.messageService.add({
            key: 'tst',
            severity: 'error',
            summary: 'Error',
            detail: this.translateService.instant('order_erp.invalid_response')
          });
        }
      },
      error: () => {
        this.loaderService.hide();
        this.messageService.add({
          key: 'tst',
          severity: 'error',
          summary: 'Error',
          detail: this.translateService.instant('order_erp.download_invoice_failed')
        });
      }
    });
  }
}